import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function GMControlsPanel({
  gameState, channelId, user,
  gmSpawnMode, onSpawnModeChange,
  gmEnemyType, onEnemyTypeChange,
  gmBossKey, onBossKeyChange,
  onRefresh,
  gmCivSpawnMode, onCivSpawnModeChange,
  gmCivType, onCivTypeChange,
}) {
  const [gmTab, setGmTab] = useState('actions');

  // Actions tab state
  const [gmFleetTarget, setGmFleetTarget] = useState('single');
  const [gmActionTarget, setGmActionTarget] = useState('');
  const [gmActionAmount, setGmActionAmount] = useState(10);
  const [gmStatusEffect, setGmStatusEffect] = useState('fire');
  const [gmTurretCount, setGmTurretCount] = useState(1);

  // Battlefield tab state
  const [gmWeather, setGmWeather] = useState('clear');
  const [gmWeatherDelay, setGmWeatherDelay] = useState(0);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);

  // Spawn-mode state is lifted to GameView so handleMapClick can read it.
  // Fall back to internal state if props are not provided (standalone usage).
  const [_spawnMode, _setSpawnMode] = useState(false);
  const [_enemyType, _setEnemyType] = useState('destroyer');
  const [_bossKey, _setBossKey] = useState('siren_boss_observer_alpha');
  const spawnMode   = gmSpawnMode    !== undefined ? gmSpawnMode    : _spawnMode;
  const setSpawnMode = onSpawnModeChange ?? _setSpawnMode;
  const enemyType   = gmEnemyType    !== undefined ? gmEnemyType    : _enemyType;
  const setEnemyType = onEnemyTypeChange ?? _setEnemyType;
  const bossKey     = gmBossKey      !== undefined ? gmBossKey      : _bossKey;
  const setBossKey   = onBossKeyChange  ?? _setBossKey;

  const [_civSpawnMode, _setCivSpawnMode] = useState(false);
  const [_civType, _setCivType] = useState('airliner');
  const civSpawnMode = gmCivSpawnMode !== undefined ? gmCivSpawnMode : _civSpawnMode;
  const setCivSpawnMode = onCivSpawnModeChange ?? _setCivSpawnMode;
  const civType      = gmCivType      !== undefined ? gmCivType      : _civType;
  const setCivType   = onCivTypeChange ?? _setCivType;

  // Civilians tab state
  const [selectedCivilian, setSelectedCivilian] = useState(null);
  const [civDestInput, setCivDestInput] = useState('');

  // AI tab state
  const [gmAIMoveCoord, setGmAIMoveCoord] = useState('');
  const [gmAIAttackTarget, setGmAIAttackTarget] = useState('');

  const isGM = gameState?.gmIds?.includes(user?.id) ?? false;
  if (!isGM) return null;

  // Derive which AI this GM is controlling from game state
  const controlledEnemy = gameState?.enemies?.find(e => e.gmControllerId === user?.id);
  const gmControlledAI = controlledEnemy
    ? { id: controlledEnemy.id, name: controlledEnemy.name || controlledEnemy.id }
    : null;

  // ── Helpers ──────────────────────────────────────────────────────────────
  const post = (path, body) =>
    axios.post(`${API_URL}/api/game/${channelId}/${path}`, body, { withCredentials: true }).catch(err => {
      console.error(`GM action failed [${path}]:`, err?.response?.data?.error || err.message);
    });

  const allUnits = [
    ...(gameState?.players ?? []).map(p => ({
      id: p.userId || p.id,
      label: (p.characterAlias || p.username || p.id) + (p.sunk ? ' (sunk)' : ''),
      type: 'player',
      sunk: p.sunk,
    })),
    ...(gameState?.enemies ?? []).map(e => ({
      id: e.id,
      label: (e.name || e.id) + (e.sunk ? ' (sunk)' : ''),
      type: 'enemy',
      sunk: e.sunk,
    })),
  ];

  const [targetId, targetType] = gmActionTarget ? gmActionTarget.split(':') : [null, null];

  const selectedUnitData = targetId
    ? (targetType === 'player'
        ? gameState?.players?.find(p => (p.userId || p.id) === targetId)
        : gameState?.enemies?.find(e => e.id === targetId))
    : null;
  const turretMax = selectedUnitData
    ? Object.values(selectedUnitData.weapons || {}).filter(w => w.type === 'main').length
    : 0;

  // ── API Handlers ──────────────────────────────────────────────────────────
  const handleDamage = () => {
    const body = gmFleetTarget !== 'single'
      ? { fleetTarget: gmFleetTarget, amount: gmActionAmount }
      : { targetId, targetType, amount: gmActionAmount };
    post('gm/damage', body);
  };

  const handleHeal = () => {
    const body = gmFleetTarget !== 'single'
      ? { fleetTarget: gmFleetTarget, amount: gmActionAmount }
      : { targetId, targetType, amount: gmActionAmount };
    post('gm/heal', body);
  };

  const handleApplyStatus = () => {
    const body = gmFleetTarget !== 'single'
      ? { fleetTarget: gmFleetTarget, status: gmStatusEffect }
      : { targetId, targetType, status: gmStatusEffect };
    post('apply-status', body);
  };

  const handleRevive = () => post('gm/revive', { targetId, targetType });

  const handleDisableComponent = (component) => {
    const alreadyDisabled =
      (component === 'engine'  && selectedUnitData?.enginesDamaged) ||
      (component === 'rudder'  && selectedUnitData?.rudderDamaged)  ||
      (component === 'turrets' && (selectedUnitData?.disabledTurrets ?? 0) > 0);

    if (alreadyDisabled) {
      post('gm/repair-component', { targetId, targetType, component });
    } else {
      post('gm/disable-component', {
        targetId,
        targetType,
        component,
        ...(component === 'turrets' ? { turretCount: gmTurretCount } : {}),
      });
    }
  };

  const handleGMToggle = () => post('gm-toggle', {});

  const handleSetWeather = () =>
    post('weather', { condition: gmWeather, turnsDelay: gmWeatherDelay });

  const handleEndBattle = () => post('end', {});
  const handlePauseBattle = async () => {
    await post('pause', {});
    if (onRefresh) onRefresh();
  };
  const handleResumeBattle = () => post('resume', {});

  const handleAIMove = () =>
    post('gm/ai-move', { aiId: gmControlledAI?.id, coordinate: gmAIMoveCoord.toUpperCase() });

  const handleAIAttack = () =>
    post('gm/ai-attack', { aiId: gmControlledAI?.id, targetId: gmAIAttackTarget });

  const handleAIEndTurn = () => post('gm/ai-end-turn', { aiId: gmControlledAI?.id });
  const handleAIRelease = () => post('gm/release-control', { aiId: gmControlledAI?.id });

  const handleSetCivDest = async () => {
    if (!selectedCivilian || !civDestInput.trim()) return;
    await post('gm/civilian/set-destination', { civId: selectedCivilian.id, destination: civDestInput.trim() });
    setCivDestInput('');
  };

  // ── Style constants ───────────────────────────────────────────────────────
  const pendingWeather = gameState?.pendingWeather;
  const weatherEmojis = { clear: '☀️', rainy: '🌧️', foggy: '🌫️', thunderstorm: '⛈️', hurricane: '🌀', snow: '🌨️', blizzard: '❄️' };

  const gmBtnBase = {
    padding: '5px 9px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    cursor: 'pointer', border: '1px solid transparent', whiteSpace: 'nowrap',
  };
  const btnDmg  = { ...gmBtnBase, background: 'rgba(237,66,69,0.15)',   color: '#f87171', borderColor: 'rgba(237,66,69,0.35)' };
  const btnHeal = { ...gmBtnBase, background: 'rgba(34,197,94,0.12)',   color: '#4ade80', borderColor: 'rgba(34,197,94,0.3)' };
  const btnGold = { ...gmBtnBase, background: 'rgba(26,106,255,0.15)',  color: '#60a5fa', borderColor: 'rgba(26,106,255,0.35)' };
  const btnBlue = { ...gmBtnBase, background: 'rgba(167,139,250,0.12)', color: '#a78bfa', borderColor: 'rgba(167,139,250,0.3)' };

  const inputStyle = {
    background: '#101e2e', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)',
    borderRadius: 4, padding: '4px 5px', fontSize: 11,
  };
  const selectStyle = { ...inputStyle, flex: 1 };
  const fieldLabelStyle = {
    color: 'rgba(255,255,255,0.4)', fontSize: 10, textTransform: 'uppercase',
    letterSpacing: '0.5px', width: 50, flexShrink: 0,
  };

  const SectionLabel = ({ children }) => (
    <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700 }}>
      {children}
    </div>
  );
  const Divider = () => (
    <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', margin: '1px 0' }} />
  );

  // ── Tab components ────────────────────────────────────────────────────────
  const isFleet = gmFleetTarget !== 'single';
  const noTarget = !isFleet && !gmActionTarget;

  const ActionsTab = () => (
    <>
      {!isFleet && (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <span style={fieldLabelStyle}>Target</span>
          <select style={selectStyle} value={gmActionTarget} onChange={e => setGmActionTarget(e.target.value)}>
            <option value="">— select —</option>
            {allUnits.map(u => (
              <option key={`${u.id}:${u.type}`} value={`${u.id}:${u.type}`}>{u.label}</option>
            ))}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', background: '#101e2e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
        {[['single', 'Single Unit'], ['friendly', '🤝 Friendly'], ['opfor', '💀 OPFOR']].map(([val, label]) => (
          <button key={val} onClick={() => setGmFleetTarget(val)} style={{
            flex: 1, padding: '4px 3px', background: gmFleetTarget === val ? 'rgba(167,139,250,0.15)' : 'none',
            border: 'none', borderRight: '1px solid rgba(255,255,255,0.06)',
            color: gmFleetTarget === val ? '#a78bfa' : 'rgba(255,255,255,0.3)',
            fontSize: 9, fontWeight: 600, cursor: 'pointer',
          }}>{label}</button>
        ))}
      </div>

      <Divider />

      <SectionLabel>Damage / Heal</SectionLabel>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <span style={fieldLabelStyle}>Amount</span>
        <input type="number" min="1" style={{ ...inputStyle, width: 60 }}
          value={gmActionAmount} onChange={e => setGmActionAmount(parseInt(e.target.value) || 1)} />
        {(() => { const dis = noTarget || selectedUnitData?.sunk; return (<>
          <button style={{ ...btnDmg,  opacity: dis ? 0.4 : 1 }} disabled={dis}  onClick={handleDamage}>Hit</button>
          <button style={{ ...btnHeal, opacity: dis ? 0.4 : 1 }} disabled={dis}  onClick={handleHeal}>Heal</button>
        </>); })()}
      </div>
      <div style={{ marginTop: 4 }}>
        {(() => { const canRevive = !isFleet && selectedUnitData?.sunk; return (
          <button style={{ ...btnGold, width: '100%', opacity: canRevive ? 1 : 0.4 }} disabled={!canRevive} onClick={handleRevive}>✨ Revive</button>
        ); })()}
      </div>

      <Divider />

      <SectionLabel>Status Effect</SectionLabel>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <select style={selectStyle} value={gmStatusEffect} onChange={e => setGmStatusEffect(e.target.value)}>
          <option value="fire">🔥 Fire</option>
          <option value="flood">💧 Flood</option>
          <option value="ammo-rack">💣 Ammo Rack</option>
          <option value="kill">💀 Kill</option>
        </select>
        <button style={{ ...btnGold, opacity: noTarget ? 0.4 : 1 }} disabled={noTarget} onClick={handleApplyStatus}>Apply</button>
      </div>

      <Divider />

      <SectionLabel>Disable Component</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
        <button style={{ ...btnBlue, opacity: (isFleet || !gmActionTarget) ? 0.4 : 1 }}
          disabled={isFleet || !gmActionTarget}
          onClick={() => handleDisableComponent('engine')}>⚙️ Engine</button>
        <button style={{ ...btnBlue, opacity: (isFleet || !gmActionTarget) ? 0.4 : 1 }}
          disabled={isFleet || !gmActionTarget}
          onClick={() => handleDisableComponent('rudder')}>🚢 Rudder</button>
      </div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <button style={{ ...btnBlue, flex: 1, opacity: (isFleet || !gmActionTarget) ? 0.4 : 1 }}
          disabled={isFleet || !gmActionTarget}
          onClick={() => handleDisableComponent('turrets')}>🔫 Turrets</button>
        <input type="number" min="1" max={turretMax || 99} style={{ ...inputStyle, width: 36, textAlign: 'center' }}
          value={gmTurretCount} onChange={e => setGmTurretCount(Math.max(1, parseInt(e.target.value) || 1))} />
        {turretMax > 0 && <span style={{ color: '#2a4060', fontSize: 9 }}>/ {turretMax}</span>}
      </div>
    </>
  );

  const BattlefieldTab = () => (
    <>
      <SectionLabel>Weather</SectionLabel>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <select style={selectStyle} value={gmWeather} onChange={e => setGmWeather(e.target.value)}>
          <option value="clear">☀️ Clear</option>
          <option value="rainy">🌧️ Rainy</option>
          <option value="foggy">🌫️ Foggy</option>
          <option value="thunderstorm">⛈️ Thunderstorm</option>
          <option value="hurricane">🌀 Hurricane</option>
          <option value="snow">🌨️ Snow</option>
          <option value="blizzard">❄️ Blizzard</option>
        </select>
        <input type="number" min="0" max="10" style={{ ...inputStyle, width: 36, textAlign: 'center' }}
          value={gmWeatherDelay} onChange={e => setGmWeatherDelay(parseInt(e.target.value) || 0)} />
        <button style={btnGold} onClick={handleSetWeather}>
          {gmWeatherDelay > 0 ? `+${gmWeatherDelay}t` : 'Set'}
        </button>
      </div>
      {pendingWeather && (
        <div style={{ background: '#101e2e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4, padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 14 }}>{weatherEmojis[pendingWeather.condition] ?? '🌤️'}</span>
          <span style={{ flex: 1, color: 'rgba(255,255,255,0.5)', fontSize: 9 }}>{pendingWeather.condition} scheduled</span>
          <span style={{ color: '#a78bfa', fontWeight: 700, fontSize: 9 }}>in {pendingWeather.turnsUntil}t</span>
        </div>
      )}

      <Divider />

      <SectionLabel>Spawn Enemy</SectionLabel>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <select style={selectStyle} value={enemyType} onChange={e => setEnemyType(e.target.value)}>
          <option value="destroyer">Destroyer</option>
          <option value="light_cruiser">Light Cruiser</option>
          <option value="heavy_cruiser">Heavy Cruiser</option>
          <option value="battlecruiser">Battlecruiser</option>
          <option value="battleship">Battleship</option>
          <option value="carrier">Carrier</option>
          <option value="submarine">Submarine</option>
          <option value="boss">👑 Boss</option>
        </select>
        <button style={spawnMode ? { ...btnGold, background: '#5a2000' } : btnGold}
          onClick={() => setSpawnMode(!spawnMode)}>
          {spawnMode ? '✕ Cancel' : 'Place'}
        </button>
      </div>
      {enemyType === 'boss' && (
        <select style={selectStyle} value={bossKey} onChange={e => setBossKey(e.target.value)}>
          <option value="siren_boss_observer_alpha">Observer α</option>
          <option value="siren_boss_tester_beta">Tester β</option>
          <option value="siren_boss_purifier">Purifier</option>
          <option value="siren_boss_dreamweaver">Dreamweaver</option>
          <option value="siren_boss_omitter">Omitter</option>
          <option value="siren_boss_complier">Compiler</option>
          <option value="siren_boss_arbiter_empress_iii">The Empress III</option>
          <option value="siren_boss_arbiter_hierophant_v">The Hierophant V</option>
          <option value="siren_boss_arbiter_lovers_vi">The Lovers VI</option>
          <option value="siren_boss_arbiter_strength_viii">Strength VIII</option>
          <option value="siren_boss_arbiter_hermit_ix">The Hermit IX</option>
          <option value="siren_boss_arbiter_temperance_xiv">Temperance XIV</option>
          <option value="siren_boss_arbiter_devil_xv">The Devil XV</option>
          <option value="siren_boss_arbiter_tower_xvi">The Tower XVI</option>
        </select>
      )}
      {spawnMode && (
        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 9, textAlign: 'center' }}>Click a map cell to place</div>
      )}

      <Divider />

      <SectionLabel>Battle Control</SectionLabel>

      {gameState?.phase === 'battle' && !showPauseConfirm && (
        <button style={{ ...btnGold, width: '100%', padding: 6, marginBottom: 4 }}
          onClick={() => setShowPauseConfirm(true)}>⏸️ Pause Battle</button>
      )}
      {showPauseConfirm && (
        <>
          <div style={{ color: '#facc15', fontSize: 10, textAlign: 'center' }}>Pause and save the battle?</div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
            <button style={{ ...btnGold, flex: 1 }}
              onClick={() => { handlePauseBattle(); setShowPauseConfirm(false); }}>Yes</button>
            <button style={{ ...btnBlue, flex: 1 }} onClick={() => setShowPauseConfirm(false)}>No</button>
          </div>
        </>
      )}

      {gameState?.phase !== 'battle' && gameState?.phase !== 'joining' && gameState?.phase !== 'ended' && (
        <button style={{ ...btnHeal, width: '100%', padding: 6, marginBottom: 4 }}
          onClick={handleResumeBattle}>▶️ Resume Battle</button>
      )}

      {!showEndConfirm ? (
        <button style={{ ...btnDmg, width: '100%', padding: 6 }}
          onClick={() => setShowEndConfirm(true)}>🔴 End Battle</button>
      ) : (
        <>
          <div style={{ color: '#f87171', fontSize: 10, textAlign: 'center' }}>End the battle?</div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button style={{ ...btnDmg, flex: 1 }}
              onClick={() => { handleEndBattle(); setShowEndConfirm(false); }}>Yes</button>
            <button style={{ ...btnBlue, flex: 1 }} onClick={() => setShowEndConfirm(false)}>No</button>
          </div>
        </>
      )}
    </>
  );

  const AITab = () => (
    <>
      {gmControlledAI ? (
        <>
          <div style={{ color: '#a78bfa', fontSize: 10, fontWeight: 700, padding: '6px 8px', background: 'rgba(167,139,250,0.1)', borderRadius: 4, border: '1px solid rgba(167,139,250,0.3)' }}>
            🕹️ Controlling: {gmControlledAI.name}
          </div>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            <span style={fieldLabelStyle}>Move to</span>
            <input style={{ ...inputStyle, flex: 1 }} value={gmAIMoveCoord}
              onChange={e => setGmAIMoveCoord(e.target.value.toUpperCase())} placeholder="B12" />
            <button style={{ ...btnBlue, opacity: !gmAIMoveCoord ? 0.4 : 1 }}
              disabled={!gmAIMoveCoord} onClick={handleAIMove}>Move</button>
          </div>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            <span style={fieldLabelStyle}>Attack</span>
            <select style={selectStyle} value={gmAIAttackTarget} onChange={e => setGmAIAttackTarget(e.target.value)}>
              <option value="">— select —</option>
              {(gameState?.players ?? []).filter(p => !p.sunk).map(p => (
                <option key={p.userId || p.id} value={p.userId || p.id}>
                  {p.characterAlias || p.username}
                </option>
              ))}
            </select>
          </div>
          <button style={{ ...btnBlue, width: '100%', opacity: !gmAIAttackTarget ? 0.4 : 1 }}
            disabled={!gmAIAttackTarget} onClick={handleAIAttack}>⚡ Attack</button>
          <Divider />
          <div style={{ display: 'flex', gap: 4 }}>
            <button style={{ ...btnDmg, flex: 1 }} onClick={handleAIEndTurn}>End AI Turn</button>
            <button style={{ ...btnBlue, flex: 1 }} onClick={handleAIRelease}>Release</button>
          </div>
        </>
      ) : (
        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, textAlign: 'center', padding: '16px 8px', fontStyle: 'italic' }}>
          Click an enemy unit on the map to take control.
        </div>
      )}
    </>
  );

  const CiviliansTab = () => {
    const civilians = gameState?.civilians || [];
    return (
      <>
        <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {civilians.length === 0 && (
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, textAlign: 'center', padding: '12px 4px', fontStyle: 'italic' }}>
              No civilians on the map
            </div>
          )}
          {civilians.map(civ => (
            <div
              key={civ.id}
              onClick={() => setSelectedCivilian(civ.alive ? (selectedCivilian?.id === civ.id ? null : civ) : null)}
              style={{
                cursor: civ.alive ? 'pointer' : 'default',
                opacity: civ.alive ? 1 : 0.4,
                padding: '2px 5px',
                borderRadius: 3,
                background: selectedCivilian?.id === civ.id ? 'rgba(167,139,250,0.15)' : 'transparent',
                display: 'flex',
                gap: 6,
                fontSize: 10,
              }}
            >
              <span style={{ flex: 1, color: 'rgba(255,255,255,0.7)' }}>{civ.type.replace(/_/g, ' ')}</span>
              <span style={{ color: 'rgba(255,255,255,0.4)' }}>{civ.position}</span>
              <span style={{ color: civ.alive ? '#4ade80' : '#f87171', minWidth: 48, textAlign: 'right' }}>
                {civ.alive ? `${civ.hp}/${civ.maxHp} HP` : 'SUNK'}
              </span>
            </div>
          ))}
        </div>
        {selectedCivilian && (
          <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
            <input
              type="text"
              placeholder="Dest (e.g. E5 or edge)"
              value={civDestInput}
              onChange={e => setCivDestInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSetCivDest()}
              style={{ ...inputStyle, flex: 1 }}
            />
            <button style={{ ...btnBlue, opacity: !civDestInput.trim() ? 0.4 : 1 }}
              disabled={!civDestInput.trim()} onClick={handleSetCivDest}>Set</button>
          </div>
        )}
        <Divider />
        <SectionLabel>Spawn Civilian</SectionLabel>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <select style={selectStyle} value={civType} onChange={e => setCivType(e.target.value)}>
            <option value="airliner">Airliner</option>
            <option value="fishing_vessel">Fishing Vessel</option>
            <option value="cargo_ship">Cargo Ship</option>
          </select>
          <button style={civSpawnMode ? { ...btnGold, background: '#5a2000' } : btnGold}
            onClick={() => setCivSpawnMode(!civSpawnMode)}>
            {civSpawnMode ? '✕ Cancel' : 'Place'}
          </button>
        </div>
        {civSpawnMode && (
          <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 9, textAlign: 'center' }}>Click a map cell to place</div>
        )}
      </>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 8,
      overflow: 'hidden',
      fontSize: 11,
      boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(10,18,30,0.6)',
        padding: '8px 12px',
        color: '#a78bfa',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '1px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}>
        ⚙️ GM CONTROLS
        <button onClick={handleGMToggle} style={{
          marginLeft: 'auto',
          padding: '2px 8px',
          background: 'rgba(237,66,69,0.2)',
          color: '#f87171',
          border: '1px solid rgba(237,66,69,0.4)',
          borderRadius: 4,
          fontSize: 9,
          fontWeight: 700,
          cursor: 'pointer',
        }}>DROP POWERS</button>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', background: 'rgba(10,18,30,0.4)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {[['actions', '⚔️ Actions'], ['battlefield', '🌍 Battlefield'], ['ai', '🕹️ AI'], ['civilians', '🚢 Civs']].map(([id, label]) => (
          <button key={id} onClick={() => setGmTab(id)} style={{
            flex: 1,
            padding: '6px 2px',
            background: 'none',
            border: 'none',
            borderBottom: gmTab === id ? '2px solid #a78bfa' : '2px solid transparent',
            color: gmTab === id ? '#a78bfa' : 'rgba(255,255,255,0.3)',
            fontSize: 10,
            fontWeight: 600,
            cursor: 'pointer',
            letterSpacing: '0.3px',
          }}>{label}</button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {gmTab === 'actions' && <ActionsTab />}
        {gmTab === 'battlefield' && <BattlefieldTab />}
        {gmTab === 'ai' && <AITab />}
        {gmTab === 'civilians' && <CiviliansTab />}
      </div>
    </div>
  );
}
